import NextAuth from "next-auth";
import CredentialsProvider from "next-auth/providers/credentials";
import { useSession as originalUseSession } from "next-auth/react";
import { decode as authJsDecode } from "next-auth/jwt";
import type { AuthenticationResponseJSON } from "@simplewebauthn/server";

import type { NextAuthConfig } from "next-auth";
import type { JWT } from "next-auth/jwt";
import type { UserType, UserResponseType } from "@/types/user";
import { AuthResult, PasskeyVerifyPayload, PasskeyLoginChallenge } from "./types";
import { NextResponse } from "next/server";

export const noAuthResponse = NextResponse.json({ error: "Not authenticated" }, { status: 401 });

declare module "next-auth" {
  interface User extends UserType {
    id?: string;
    name?: string | null;
    email?: string | null;
    emailVerified?: Date | null;
    avatar: string | null;
    premiumSubscription: boolean;
    accessToken: string;
    refreshToken: string;
    subId: string;
    sessionId?: string;
    // V2 fields
    smart_account_address?: string;
    p256PublicKeyHex?: string;
    credentialId?: string;
    hasWallet?: boolean;
    hasPasskey?: boolean;
    passkeyTransports?: string[];
  }

  interface Session {
    user: {
      id: string;
      name: string;
      email: string;
      avatar: string | null;
      premiumSubscription: boolean;
      accessToken: string;
      refreshToken: string;
      subId: string;
      emailVerified?: Date | null;
      sessionId?: string;
      // V2 fields
      smart_account_address?: string;
      p256PublicKeyHex?: string;
      credentialId?: string;
      hasWallet?: boolean;
      hasPasskey?: boolean;
      passkeyTransports?: string[];
    };
    authError?: string;
  }
}

async function fetchPasskeyLogin(payload: PasskeyVerifyPayload): Promise<AuthResult> {
  console.log("[fetchPasskeyLogin] Verifying passkey challenge...");
  if (!process.env.AUTH_API_URL || !process.env.AUTH_API_KEY) {
    throw new Error("Missing AUTH_API_URL or AUTH_API_KEY in environment");
  }

  const res = await fetch(`${process.env.AUTH_API_URL}/auth/passkey/login/verify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": process.env.AUTH_API_KEY,
    },
    body: JSON.stringify({
      challengeId: payload.challengeId,
      response: payload.response,
    }),
  });

  const data = (await res.json().catch(() => ({}))) as AuthResult;
  console.log("[fetchPasskeyLogin] Backend response:", data);

  if (!res.ok) {
    return {
      success: false,
      error: data.error ?? `Passkey verify failed with status ${String(res.status)}`,
    };
  }

  return data;
}

declare module "next-auth/jwt" {
  interface JWT {
    id: string;
    name: string;
    email: string;
    avatar: string | null;
    premiumSubscription: boolean;
    accessToken: string;
    refreshToken: string;
    subId: string;
    sessionId?: string;
    exp?: number;
    error?: string;
    // V2 fields
    smart_account_address?: string;
    p256PublicKeyHex?: string;
    credentialId?: string;
    hasWallet?: boolean;
    hasPasskey?: boolean;
    passkeyTransports?: string[];
  }
}

export const authOptions: NextAuthConfig = {
  trustHost: true,
  providers: [
    CredentialsProvider({
      id: "credentials-srp",
      name: "SRP",
      credentials: {
        id: { label: "User ID", type: "text" },
        email: { label: "Email", type: "text" },
        accessToken: { label: "AccessToken", type: "text" },
        refreshToken: { label: "RefreshToken", type: "text" },
        subId: { label: "Sub ID", type: "text" },
        avatar: { label: "Avatar", type: "text" },
        premiumSubscription: { label: "Premium", type: "text" },
        sessionId: { label: "Session ID", type: "text" },
      },
      authorize: async (cred): Promise<UserType | null> => {
        const id = typeof cred.id === "string" ? cred.id : "";
        const email = typeof cred.email === "string" ? cred.email : "";
        const accessToken = typeof cred.accessToken === "string" ? cred.accessToken : "";
        const refreshToken = typeof cred.refreshToken === "string" ? cred.refreshToken : "";
        if (!id || !email || !accessToken || !refreshToken) return null;

        const premium =
          typeof cred.premiumSubscription === "string" ? cred.premiumSubscription === "true" : false;

        // this is wrong shouldn't be a dynamic import.
        const mappedUser: UserResponseType = {
          id,
          name: email.split("@")[0],
          email,
          avatar: typeof cred.avatar === "string" ? cred.avatar : "",
          premium_subscription: premium,
          accessToken,
          refreshToken,
          sub_id: typeof cred.subId === "string" ? cred.subId : "",
          //emailVerified: null,
          sessionId: typeof cred.sessionId === "string" ? cred.sessionId : undefined,
        };

        // Build user now, but validate tokens before returning it
        const user = createUser(mappedUser);

        // Do a fast server-side token check (same rules you use elsewhere)
        const tmpToken: JWT = {
          id: user.id,
          name: user.name,
          email: user.email,
          avatar: user.avatar,
          premiumSubscription: user.premiumSubscription,
          accessToken: user.accessToken,
          refreshToken: user.refreshToken,
          subId: user.subId,
          sessionId: user.sessionId,
        };

        const ok = await verifyAccessToken(tmpToken);
        if (!ok) {
          const refreshed = await refreshAccessToken(tmpToken);
          if (refreshed.error) return null; // hard fail if we can’t refresh
          // replace with fresh values so jwt/session get the good ones
          user.accessToken = refreshed.accessToken;
          user.refreshToken = refreshed.refreshToken;
        }

        return user;
      },
    }),
    CredentialsProvider({
      id: "passkey",
      name: "Passkey",
      credentials: {
        challengeId: { label: "Challenge ID", type: "text" },
        response: { label: "Response JSON", type: "text" },
      },
      authorize: async (cred): Promise<UserType | null> => {
        const challengeId = typeof cred.challengeId === "string" ? cred.challengeId : "";
        const responseRaw = typeof cred.response === "string" ? cred.response : "";
        if (!challengeId || !responseRaw) {
          console.error("[NextAuth:Passkey] Missing challengeId or response");
          return null;
        }

        let response: AuthenticationResponseJSON;
        try {
          response = JSON.parse(responseRaw) as AuthenticationResponseJSON;
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : String(err);
          console.error("[NextAuth:Passkey] Failed to parse response JSON:", message);
          return null;
        }

        const result = await fetchPasskeyLogin({ challengeId, response });
        if (!result.success || !result.accessToken || !result.refreshToken || !result.email || !result.id) {
          console.error("[NextAuth:Passkey] Backend verification failed:", result.error ?? "Unknown error");
          return null;
        }

        const userData: UserResponseType = {
          id: result.id,
          email: result.email,
          name: result.email.split("@")[0],
          avatar: "",
          premium_subscription: false,
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          sub_id: "",
          sessionId: result.sessionId,
        };

        return createUser(userData);
      },
    }),
    // ---------- V2 Providers ----------
    CredentialsProvider({
      id: "passkey-v2",
      name: "Passkey V2",
      credentials: {
        session: { label: "JWE Session", type: "text" },
        userId: { label: "User ID", type: "text" },
        email: { label: "Email", type: "text" },
        hasWallet: { label: "Has Wallet", type: "text" },
        smartAccountAddress: { label: "Smart Account Address", type: "text" },
        p256PublicKeyHex: { label: "P256 Public Key", type: "text" },
        credentialId: { label: "Credential ID", type: "text" },
        passkeyTransports: { label: "Passkey Transports", type: "text" },
      },
      authorize: async (cred): Promise<UserType | null> => {
        const session = typeof cred.session === "string" ? cred.session : "";
        const userId = typeof cred.userId === "string" ? cred.userId : "";
        if (!session || !userId) {
          console.error("[NextAuth:PasskeyV2] Missing session or userId");
          return null;
        }
        const email = typeof cred.email === "string" ? cred.email : "";
        let transports: string[] | undefined;
        if (typeof cred.passkeyTransports === "string" && cred.passkeyTransports) {
          try { transports = JSON.parse(cred.passkeyTransports) as string[]; } catch { /* ignore */ }
        }
        return {
          id: userId,
          name: email ? email.split("@")[0] : "User",
          email: email || "",
          avatar: "",
          premiumSubscription: false,
          accessToken: session,
          refreshToken: "",
          subId: "",
          emailVerified: null,
          sessionId: undefined,
          smart_account_address: typeof cred.smartAccountAddress === "string" ? cred.smartAccountAddress : undefined,
          p256PublicKeyHex: typeof cred.p256PublicKeyHex === "string" ? cred.p256PublicKeyHex : undefined,
          credentialId: typeof cred.credentialId === "string" ? cred.credentialId : undefined,
          hasWallet: cred.hasWallet === "true",
          hasPasskey: true,
          passkeyTransports: transports,
        };
      },
    }),
    CredentialsProvider({
      id: "magic-link-v2",
      name: "Magic Link V2",
      credentials: {
        session: { label: "JWE Session", type: "text" },
        userId: { label: "User ID", type: "text" },
        email: { label: "Email", type: "text" },
        hasWallet: { label: "Has Wallet", type: "text" },
        hasPasskey: { label: "Has Passkey", type: "text" },
        isNewUser: { label: "Is New User", type: "text" },
      },
      authorize: async (cred): Promise<UserType | null> => {
        const session = typeof cred.session === "string" ? cred.session : "";
        const userId = typeof cred.userId === "string" ? cred.userId : "";
        const email = typeof cred.email === "string" ? cred.email : "";
        if (!session || !userId) {
          console.error("[NextAuth:MagicLinkV2] Missing session or userId");
          return null;
        }
        return {
          id: userId,
          name: email ? email.split("@")[0] : "User",
          email,
          avatar: "",
          premiumSubscription: false,
          accessToken: session,
          refreshToken: "",
          subId: "",
          emailVerified: null,
          sessionId: undefined,
          hasWallet: cred.hasWallet === "true",
          hasPasskey: cred.hasPasskey === "true",
        };
      },
    }),
    // ---------- Legacy Providers ----------
    CredentialsProvider({
      id: "credentials",
      name: "Credentials",
      credentials: {
        username: { label: "Email", type: "text" },
        password: { label: "Password", type: "password" },
      },
      authorize: async (credentials): Promise<UserType | null> => {
        try {
          const email = typeof credentials.username === "string" ? credentials.username : "";
          const password = typeof credentials.password === "string" ? credentials.password : "";

          if (!email || !password) return null;
          if (!process.env.AUTH_API_URL) {
            throw new Error("Missing AUTH_API_URL in environment");
          }
          const userResponse = await fetchCredentialsUser(
            `${process.env.AUTH_API_URL}/auth/signin`,
            email,
            password,
          );
          return userResponse ? createUser(userResponse) : null;
        } catch (error: unknown) {
          if (error instanceof Error) {
            console.error("Error during credentials authentication:", error.message);
          } else {
            console.error("Unknown error during credentials authentication", error);
          }
          return null;
        }
      },
    }),
    CredentialsProvider({
      id: "social",
      name: "Social OAuth",
      credentials: {
        provider: { label: "Provider", type: "text" },
        code: { label: "Code", type: "text" },
        state: { label: "State", type: "text" },
        // baseurl: { label: "Base URL", type: "text" },
      },
      authorize: async (credentials): Promise<UserType | null> => {
        const provider = typeof credentials.provider === "string" ? credentials.provider : "";
        const code = typeof credentials.code === "string" ? credentials.code : "";
        const state = typeof credentials.state === "string" ? credentials.state : "";
        console.log("[NextAuth:Social] Received provider/code/state:", provider, code, state);

        if (!provider || !code) {
          console.error("Missing provider or code for social login");
          return null;
        }
        try {
          const response = await fetchSocialUser(provider, code, state);
          console.log("[NextAuth:Social] fetchSocialUser result:", response);

          if (!response.success || !response.user) {
            console.error("[NextAuth:Social] Social authentication failed:", response.error);
            return null; // Return null or handle error appropriately (e.g., throw or redirect)
          }

          // Create and return user
          return createUser(response.user);
        } catch (error: unknown) {
          if (error instanceof Error) {
            console.error("[NextAuth:Social] Error during social authentication:", error.message);
          } else {
            console.error("[NextAuth:Social] Unknown error during social authentication:", error);
          }
          return null; // Return null or handle error appropriately
        }
      },
    }),
  ],
  callbacks: {
    async jwt({ token, user, trigger, session }) {
      // Only try to set token properties if user exists (during sign in)
       
      if (user) {
        token.id = user.id ?? "";
        token.avatar = user.avatar;
        token.picture = user.avatar;
        token.name = user.name ?? "";
        token.email = user.email ?? "";
        token.premiumSubscription = user.premiumSubscription;
        token.accessToken = user.accessToken;
        token.refreshToken = user.refreshToken;
        token.subId = user.subId;
        token.sessionId = user.sessionId;
        // V2 fields
        token.smart_account_address = user.smart_account_address;
        token.p256PublicKeyHex = user.p256PublicKeyHex;
        token.credentialId = user.credentialId;
        token.hasWallet = user.hasWallet;
        token.hasPasskey = user.hasPasskey;
        token.passkeyTransports = user.passkeyTransports;
      }

      // Handle session.update() calls — e.g., after wallet deployment
      if (trigger === "update" && session) {
        const s = session as Record<string, unknown>;
        if (typeof s.smart_account_address === "string") {
          token.smart_account_address = s.smart_account_address;
          token.hasWallet = true;
        }
        if (typeof s.credentialId === "string") {
          token.credentialId = s.credentialId;
          token.hasPasskey = true;
        }
        if (typeof s.p256PublicKeyHex === "string") {
          token.p256PublicKeyHex = s.p256PublicKeyHex;
        }
        if (Array.isArray(s.passkeyTransports)) {
          token.passkeyTransports = s.passkeyTransports as string[];
        }
      }
      try {
        return await validateAuthorization(token);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        console.error("[jwt callback] validateAuthorization error:", message);
        token.error = "ValidateException";
        return token;
      }
    },

    async session({ session, token }) {
      try {
        const validAccess = await validateAuthorization(token);
        const baseUser = {
          id: validAccess.id,
          avatar: validAccess.avatar ?? null,
          name: validAccess.name,
          email: validAccess.email,
          premiumSubscription: validAccess.premiumSubscription,
          accessToken: validAccess.accessToken,
          refreshToken: validAccess.refreshToken,
          subId: validAccess.subId,
          emailVerified: null as Date | null,
          sessionId: validAccess.sessionId,
          // V2 fields
          smart_account_address: validAccess.smart_account_address,
          p256PublicKeyHex: validAccess.p256PublicKeyHex,
          credentialId: validAccess.credentialId,
          hasWallet: validAccess.hasWallet,
          hasPasskey: validAccess.hasPasskey,
          passkeyTransports: validAccess.passkeyTransports,
        };
        if (validAccess.error) {
          session.user = baseUser;
          session.authError = validAccess.error;
          return session;
        }
        session.user = baseUser;
        return session;
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        console.error("[session callback] validateAuthorization unexpected error:", errorMessage);
        session.user = {
          id: "",
          avatar: null,
          name: "",
          email: "",
          premiumSubscription: false,
          accessToken: "",
          refreshToken: "",
          subId: "",
          emailVerified: null,
          sessionId: undefined,
        };
        session.authError = "ValidateException";
        return session;
      }
    },

    async redirect({ url, baseUrl }) {
      return url.startsWith(baseUrl) ? url : baseUrl;
    },
  },

  pages: {
    signIn: "/auth",
  },

  session: {
    strategy: "jwt",
  },
};

export async function validateAuthorization(token: JWT): Promise<JWT> {
  try {
    const validAccess = await verifyAccessToken(token);
    if (!validAccess) {
      console.log("access token invalid", token);
      const refreshedToken = await refreshAccessToken(token);
      if (!refreshedToken.error) {
        token.accessToken = refreshedToken.accessToken;
        token.refreshToken = refreshedToken.refreshToken;
      } else {
        // mark token with the refresh error (JWT has optional error?: string)
        token.error = refreshedToken.error;
        console.error(
          "[validateAuthorization] Failed to refresh access token. Server responded with error:",
          refreshedToken.error,
        );
      }
    }
  } catch (err) {
    // Ensure we never throw from validateAuthorization
    console.error("[validateAuthorization] unexpected error:", err);
    token.error = "ValidateException";
  }
  return token;
}

export async function verifyAccessToken(token: JWT): Promise<boolean> {
  const decodeToken = async (jwe: string) => {
    try {
      if (!process.env.JWT_SECRET || !process.env.SALT_SECRET) {
        throw new Error("Missing JWT_SECRET / SALT_SECRET in environment");
      }
      return await authJsDecode({
        token: jwe,
        secret: process.env.JWT_SECRET,
        salt: process.env.SALT_SECRET,
      });
    } catch (error) {
      console.error("[verifyAccessToken] Error decoding token =>", error);
      return false;
    }
  };

  const currentTime = Math.floor(Date.now() / 1000);

  // V2 sessions: no refreshToken, just check the JWE accessToken
  if (token.refreshToken === "") {
    const at = await decodeToken(token.accessToken);
    if (!at || !at.exp) return false;
    return at.exp > currentTime;
  }

  // V1 sessions: check both accessToken and refreshToken
  const at = await decodeToken(token.accessToken);
  const rt = await decodeToken(token.refreshToken);
  if (!at || !rt) return false;
  if (!at.exp || !rt.exp) return false;
  if (token.exp && token.exp < currentTime) return false;
  if (at.exp < currentTime || rt.exp < currentTime) return false;

  return true;
}

interface tokenValidationResponse {
  success: boolean;
  error?: string;
}

async function refreshAccessToken(token: JWT): Promise<JWT> {
  // V2 sessions have no refresh token — user must re-authenticate
  if (token.refreshToken === "") {
    return { ...token, error: "SessionExpired" };
  }

  let res: Response;
  try {
    if (!process.env.AUTH_API_URL || !process.env.AUTH_API_KEY) {
      throw new Error("Missing AUTH_API_URL or AUTH_API_KEY in environment");
    }
    res = await fetch(`${process.env.AUTH_API_URL}/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": process.env.AUTH_API_KEY,
      },
      body: JSON.stringify({ refreshToken: token.refreshToken }),
    });
  } catch (err) {
    // ADDED: More logging if the fetch itself fails (e.g. ECONNREFUSED)
    console.error("[refreshAccessToken] fetch failed =>", err);
    return { ...token, error: "FetchError" };
  }

  if (!res.ok) {
    // ADDED: Log the status and text for better debugging
    const text = await res.text().catch(() => "Unknown body");
    console.error("[refreshAccessToken] refresh call failed with status:", res.status, text);
    return { ...token, error: "RefreshAccessTokenError" };
  }

  const data = (await res.json().catch(() => ({}))) as AuthResult;
  console.log("[refreshAccessToken] refresh endpoint responded:", data);

  if (!data.success || !data.refreshToken) {
    console.error("[refreshAccessToken] Missing success or refreshToken in response:", data);
    return { ...token, error: "RefreshAccessTokenError" };
  }

  return {
    ...token,
    accessToken: data.accessToken ?? token.accessToken,
    refreshToken: data.refreshToken,
  };
}

async function fetchCredentialsUser(
  url: string,
  email: string,
  password: string,
): Promise<UserResponseType | null> {
  try {
    if (!process.env.AUTH_API_KEY) {
      throw new Error("Missing AUTH_API_KEY in environment");
    }
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": process.env.AUTH_API_KEY,
      },
      body: JSON.stringify({ email: email, password: password }),
    });

    // Get the response data
    const data = (await res.json()) as AuthResult;

    if (!res.ok) {
      // Extract error message from response if available
      const errorMessage = data.error ?? `Authentication failed with status: ${String(res.status)}`;
      console.error("Authentication error:", errorMessage);
      // Throw error with message to be handled by NextAuth
      throw new Error(errorMessage);
    }

    console.log("fetchCredentialsUser");
    console.log("fetchCredentialsUser", data);

    if (!data.success || !data.accessToken || !data.refreshToken || !data.email || !data.id) {
      throw new Error("Invalid response format from authentication server");
    }

    // todo: unifiy the types better.
    return {
      id: data.id,
      name: data.email.split("@")[0],
      email: data.email,
      avatar: "",
      premium_subscription: false,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      sub_id: "",
      sessionId: data.sessionId,
    };
  } catch (error) {
    console.error("Error in fetchCredentialsUser:", error);
    throw error; // Re-throw to be handled by NextAuth
  }
}

interface SocialUserResponse {
  success: boolean;
  user?: UserResponseType;
  error?: string;
}

async function fetchSocialUser(
  provider: string,
  code: string,
  state: string,
): Promise<SocialUserResponse> {
  console.log("[fetchSocialUser] Starting exchange logic...");
  if (!process.env.AUTH_API_URL || !process.env.AUTH_API_KEY) {
    throw new Error("Missing AUTH_API_URL or AUTH_API_KEY in environment");
  }
  // For OAuth login flow, we should always use the login endpoint
  // We don't need to check for an existing session as this is a fresh login attempt
  const url = `${process.env.AUTH_API_URL}/auth/oauth/login`;

  console.log(
    `[fetchSocialUser] Calling express backend at ${url} with provider/code/state:`,
    provider,
    code,
    state,
  );

  // Prepare headers
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "x-api-key": process.env.AUTH_API_KEY,
  };

  // Make the request
  try {
    const res = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ provider, code, state }),
    });

    const data = (await res.json()) as AuthResult;
    console.log("[fetchSocialUser] Express backend responded with:", data);

    if (!res.ok) {
      console.error("[fetchSocialUser] Backend responded with error:", data);
      return {
        success: false,
        error: data.error ?? `Error communicating with backend. Status: ${String(res.status)}`,
      };
    }

    // Validate the response structure
    if (!data.id || !data.email) {
      console.error("[fetchSocialUser] Missing user data in backend response:", data);
      return {
        success: false,
        error: data.error ?? "Backend response missing required user data",
      };
    }

    if (!data.accessToken || !data.refreshToken) {
      console.error("[fetchSocialUser] Missing tokens in backend response:", data);
      return {
        success: false,
        error: data.error ?? "Backend response missing required tokens",
      };
    }

    // Construct the user object
    const userData: UserResponseType = {
      id: data.id,
      email: data.email,
      name: data.email.split("@")[0],
      avatar: "", // needs implemented
      premium_subscription: false,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      sub_id: "",
      sessionId: data.sessionId,
    };

    console.log("[fetchSocialUser] Completed user data:", userData);
    return {
      success: true,
      user: userData,
    };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    console.error("[fetchSocialUser] Error during fetch:", message);
    return {
      success: false,
      error: message,
    };
  }
}

function createUser(user: UserResponseType): UserType {
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    avatar: user.avatar,
    premiumSubscription: user.premium_subscription,
    accessToken: user.accessToken,
    refreshToken: user.refreshToken,
    subId: user.sub_id || "",
    emailVerified: null,
    sessionId: user.sessionId,
  };
}

export async function requestPasskeyLoginOptions(email: string): Promise<PasskeyLoginChallenge> {
  console.log("[requestPasskeyLoginOptions] Requesting challenge for:", email);
  if (!process.env.AUTH_API_URL || !process.env.AUTH_API_KEY) {
    throw new Error("Missing AUTH_API_URL or AUTH_API_KEY in environment");
  }

  const res = await fetch(`${process.env.AUTH_API_URL}/auth/passkey/login/options`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": process.env.AUTH_API_KEY,
    },
    body: JSON.stringify({ email }),
  });

  if (!res.ok) {
    const errorText = await res.text().catch(() => "unknown_error");
    throw new Error(`Failed to request passkey login challenge (${String(res.status)}): ${errorText}`);
  }

  return (await res.json()) as PasskeyLoginChallenge;
}

export async function verifyPasskeyLogin(payload: PasskeyVerifyPayload): Promise<AuthResult> {
  return fetchPasskeyLogin(payload);
}

export const { handlers, auth, signIn, signOut } = NextAuth(authOptions);
export const useSession = originalUseSession;
